import { useEffect, useState } from 'react';

import { breakpoints } from '@hangar31/rc-layout';

// The md breakpoint is where the admin UI switches between its mobile and
// desktop layouts — the sidebar force-condenses below it, and the desktop
// handoff banner is only shown below it. Kept as one constant so the JS
// checks can't drift from the `mq` media queries in the styles.
export const DESKTOP_MEDIA_QUERY = `(min-width: ${breakpoints.md}px)`;

/**
 * True when the viewport is at or above the md breakpoint.
 *
 * Always false on the server and on the first client render so hydration
 * matches; the real value lands in an effect immediately after mount, and
 * tracks resizes after that.
 *
 * Use this instead of hiding an element with a `display: none` media query
 * when the component ALSO has a side effect (an analytics capture, a fetch)
 * that must not run when the element isn't really on screen — CSS hides the
 * markup but the component still mounts and still fires its effects. That
 * exact gap made `desktopHandoffShown` fire for desktop admins who could
 * never see the banner: 539 of 593 impressions were phantom.
 */
export function useIsDesktopViewport(): boolean {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mql = window.matchMedia(DESKTOP_MEDIA_QUERY);

    setIsDesktop(mql.matches);

    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);

      return () => mql.removeEventListener('change', onChange);
    }
    // Older Safari fallback.
    mql.addListener(onChange);

    return () => mql.removeListener(onChange);
  }, []);

  return isDesktop;
}
